import React, { use, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  validateTokenFromStorage,
  loginWithSessionToken,
} from "../reducers/Login";
import Loadable from "../components/Loader/Loadable";
import AuthErrorModal from "../components/AuthErrorModal";

const AuthGuard = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [showAuthErrorModal, setShowAuthErrorModal] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [forceReAuth, setForceReAuth] = useState(false);
  const isAuthenticated = useSelector((state) => state.Login.isAuthenticated);

  useEffect(() => {
    // Extrai sessionToken da URL
    const urlParams = new URLSearchParams(location.search);
    const sessionTokenFromUrl = urlParams.get("sessionToken");
    console.log("AuthGuard - sessionTokenFromUrl:", sessionTokenFromUrl);

    // Se tem sessionToken na URL, limpa localStorage imediatamente
    if (sessionTokenFromUrl) {
      try {
        console.log("Limpando localStorage antes do login com sessionToken...");
        localStorage.clear();
        console.log("localStorage limpo com sucesso");
        // Força re-execução da autenticação
        setIsLoading(true);
        setForceReAuth(true);
      } catch (error) {
        console.error("Erro ao limpar localStorage:", error);
      }
    }
  }, [location.search, dispatch]);

  console.log("AuthGuard - isAuthenticated:", isAuthenticated);
  const isForbidden = useSelector((state) => state.Login.isForbidden);

  const redirectToCanal = () => {
    const canalTarifarioUrl =
      process.env.REACT_APP_CANAL_TARIFARIO_URL || "https://www.copel.com";
    window.location.href = canalTarifarioUrl;
  };

  const showAuthError = (message) => {
    setAuthErrorMessage(message);
    setShowAuthErrorModal(true);
    setIsLoading(false);
  };

  const handleAuthErrorClose = () => {
    setIsLoading(true);
    redirectToCanal();
  };

  useEffect(() => {
    // Se já está autenticado e não está forçando re-auth, não faz nada
    if (isAuthenticated && !forceReAuth) {
      setIsLoading(false);
      return;
    }

    // Se já está mostrando erro ou redirecionando, não executa novamente
    if (showAuthErrorModal) {
      return;
    }

    const checkAuthentication = async () => {
      try {
        // Reset force re-auth flag
        setForceReAuth(false);
        
        // Extrai sessionToken da URL
        const urlParams = new URLSearchParams(location.search);
        const sessionTokenFromUrl = urlParams.get("sessionToken");
        console.log("AuthGuard - sessionTokenFromUrl:", sessionTokenFromUrl);

        // Se tem sessionToken na URL, tenta autenticar
        if (sessionTokenFromUrl) {
          try {
            await dispatch(loginWithSessionToken(sessionTokenFromUrl)).unwrap();

            // Remove o parâmetro da URL
            const newParams = new URLSearchParams(location.search);
            newParams.delete("sessionToken");
            const newSearch = newParams.toString();
            const newUrl = `${location.pathname}${
              newSearch ? `?${newSearch}` : ""
            }`;
            window.history.replaceState({}, "", newUrl);

            setIsLoading(false);
            return;
          } catch (error) {
            const errorMessage =
              error?.message || "Falha na autenticação com token da URL";
            showAuthError(errorMessage);
            console.error(
              "Erro na autenticação com sessionToken da URL:",
              error,
              sessionTokenFromUrl,
              errorMessage
            );
            return;
          }
        }

        // Se não tem sessionToken na URL, tenta validar token do localStorage
        try {
          await dispatch(validateTokenFromStorage()).unwrap();
          setIsLoading(false);
          return;
        } catch (error) {
          console.error("Erro na validação do token:", error);
          const errorMessage = error?.message || "Token inválido ou expirado";
          showAuthError(errorMessage);
          return;
        }
      } catch (error) {
        console.error("Erro no processo de autenticação:", error);
        const errorMessage =
          error?.message || "Erro inesperado durante a autenticação";
        showAuthError(errorMessage);
      }
    };

    checkAuthentication();
  }, [
    dispatch,
    location.search,
    location.pathname,
    isAuthenticated,
    showAuthErrorModal,
    forceReAuth,
  ]);

  // Redireciona se estiver proibido
  useEffect(() => {
    if (isForbidden) {
      redirectToCanal();
    }
  }, [isForbidden]);

  // Mostra loading
  if (isLoading) {
    return <Loadable />;
  }

  // Mostra modal de erro
  if (showAuthErrorModal) {
    return (
      <AuthErrorModal
        open={showAuthErrorModal}
        onClose={handleAuthErrorClose}
        errorMessage={authErrorMessage}
      />
    );
  }

  // Se não está autenticado e não está mostrando erro, redireciona
  if (!isAuthenticated) {
    redirectToCanal();
    return null;
  }

  return children;
};

export default AuthGuard;
